import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { BRAND, openExternal } from '../../brand';
import { documents, DocKey, LegalDoc } from './documents';
import './index.css';

/** Tabs order as on the old comabooks.org/policies page. */
const TAB_ORDER: DocKey[] = ['offer', 'privacy', 'terms'];
const tabs: LegalDoc[] = TAB_ORDER.map((k) => documents.find((d) => d.key === k)!);

/** "#privacy" / "#privacy-3" → "privacy"; anything else → null. */
const docFromHash = (hash: string): DocKey | null => {
  const id = hash.replace(/^#/, '');
  const key = id.split('-')[0] as DocKey;
  return TAB_ORDER.includes(key) ? key : null;
};

/**
 * /terms (and /policies): public offer, privacy policy and terms of use.
 * Texts are in ./documents (ported verbatim from the old Comabooks site).
 */
const Terms: React.FC = () => {
  const location = useLocation();
  const [active, setActive] = useState<DocKey>(() => docFromHash(location.hash) ?? 'offer');

  useEffect(() => {
    document.title = `Документы и условия — ${BRAND.name}`;
  }, []);

  // Follow hash changes (#offer, #privacy-3, ...) and scroll to a section.
  useEffect(() => {
    const key = docFromHash(location.hash);
    if (key) setActive(key);
    const id = location.hash.slice(1);
    requestAnimationFrame(() => {
      const el = id.includes('-') ? document.getElementById(id) : null;
      if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
      else window.scrollTo(0, 0);
    });
  }, [location.hash]);

  const current = useMemo(() => documents.find((d) => d.key === active)!, [active]);

  const selectTab = (key: DocKey) => {
    setActive(key);
    window.history.replaceState(null, '', `#${key}`);
    window.scrollTo(0, 0);
  };

  return (
    <main className="terms">
      <header className="terms__head">
        <a href="/" className="terms__brand">{BRAND.wordmark}</a>
      </header>

      <div className="terms__wrap">
        <section className="terms__hero">
          <div className="terms__eyebrow">Правовая информация</div>
          <h1 className="terms__title">Документы и условия</h1>
          <p className="terms__subtitle">
            Политика конфиденциальности, условия использования сайта и
            публичная оферта ТОО «COMABOOKS».
          </p>
        </section>

        <div className="terms__tabs" role="tablist">
          {tabs.map((doc) => (
            <button
              key={doc.key}
              type="button"
              role="tab"
              aria-selected={active === doc.key}
              className={`terms__tab${active === doc.key ? ' terms__tab--active' : ''}`}
              onClick={() => selectTab(doc.key)}
            >
              {doc.label}
            </button>
          ))}
        </div>

        <article className="terms__card">
          <h2 className="terms__doc-title">{current.title}</h2>
          {current.intro && <p className="terms__doc-intro">{current.intro}</p>}

          <nav className="terms__toc" aria-label="Содержание">
            <div className="terms__toc-label">Содержание</div>
            <ol>
              {current.sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`}>{s.title}</a>
                </li>
              ))}
            </ol>
          </nav>

          {current.sections.map((s) => (
            <section key={s.id} id={s.id} className="terms__section">
              <h3>{s.title}</h3>
              <div className="terms__section-body">{s.content}</div>
            </section>
          ))}
        </article>

        {BRAND.supportUrl && (
          <div className="terms__contact">
            <div>
              <div className="terms__eyebrow">Остались вопросы?</div>
              <div className="terms__contact-title">Напишите нам — ответим в течение нескольких минут</div>
            </div>
            <button type="button" className="terms__contact-btn" onClick={() => openExternal(BRAND.supportUrl)}>
              Написать нам
            </button>
          </div>
        )}

        <footer className="terms__footer">
          <span>{BRAND.email}</span>
          <span>ТОО COMABOOKS, {new Date().getFullYear()}</span>
        </footer>
      </div>
    </main>
  );
};

export default Terms;
